// TimerContext.jsx
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useContext as useReactContext } from "react";
import { SocketContext } from "./SocketProvider";
import { DEFAULT_ROOM } from "../constants/gameStages";

/**
 * TimerContext provides a countdown timer functionality.
 * It initializes a timer with a default duration and provides methods to access the remaining time,
 * reset the timer, and check if the time has expired.
 * Components can use the `useTimer` hook to access the context.
 */

const TimerContext = createContext();

export const TimerProvider = ({ children, duration = 120 }) => {
  const { socket } = useReactContext(SocketContext);
  const [timerState, setTimerState] = useState({ start_time: null, duration });
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const timerInterval = useRef(null);

  // Listen for timer_update and timer_reset events from backend
  useEffect(() => {
    function handleTimerUpdate({ start_time, duration }) {
      setTimerState({ start_time, duration });
    }
    function handleTimerReset({ duration }) {
      setTimerState({ start_time: null, duration });
      setTimeRemaining(duration);
    }
    function handleTimerPaused({ duration }) {
      setTimerState({ start_time: null, duration });
      setTimeRemaining(duration);
    }
    socket.on("timer_update", handleTimerUpdate);
    socket.on("timer_reset", handleTimerReset);
    socket.on("timer_paused", handleTimerPaused);
    // Request current timer state on mount
    socket.emit("get_timer_state", { room: DEFAULT_ROOM });
    return () => {
      socket.off("timer_update", handleTimerUpdate);
      socket.off("timer_reset", handleTimerReset);
      socket.off("timer_paused", handleTimerPaused);
    };
  }, [socket]);

  // Calculate timeRemaining from shared timerState
  useEffect(() => {
    if (!timerState.start_time) {
      // When timer is paused/stopped, use timerState.duration instead of prop duration
      setTimeRemaining(timerState.duration || duration);
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      return;
    }
    function updateTime() {
      const now = Date.now() / 1000;
      const remaining = Math.max(0, Math.round(timerState.duration - (now - timerState.start_time)));
      setTimeRemaining(remaining);
    }
    updateTime();
    timerInterval.current = setInterval(updateTime, 1000);
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
    };
  }, [timerState, duration]);

  const isExpired = timeRemaining === 0;

  return (
    <TimerContext.Provider value={{ timeRemaining, setTimeRemaining, isExpired, timerState }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => useContext(TimerContext);
